import { Button } from "@/components/ui/button";
import type { ConnectorSummary } from "@/lib/api";
import { STATUS_LABEL, awaitsReview } from "@/lib/connectors";
import { cn } from "@/lib/utils";
import { Link } from "@tanstack/react-router";
import { ConnectorMonogram, SourceLabel } from "./parts";

// Connectors as tiles in the integrations browser's Custom view, built like
// its catalog tiles.

// The trailing word on a tile, quiet like the catalog's connected dot: only
// what needs a person, or what stopped, says anything.
function TileStatus({ c }: { c: ConnectorSummary }) {
  const hosted = c.source.kind !== "boo";
  const label =
    c.status === "live"
      ? awaitsReview(c)
        ? "New version"
        : null
      : c.status === "in_review"
        ? hosted
          ? "Waiting for merge"
          : "Waiting for approval"
        : STATUS_LABEL[c.status];
  if (!label) return null;
  const needsPerson = awaitsReview(c);
  return (
    <span className="inline-flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
      <span className={cn("size-1.5 rounded-full", needsPerson ? "bg-amber-500" : "bg-muted-foreground/40")} />
      {label}
    </span>
  );
}

function ConnectorTile({ c, orgId, containerId }: { c: ConnectorSummary; orgId: string; containerId: string }) {
  return (
    <Link
      to="/orgs/$orgId/containers/$containerId/integrations/custom/$slug"
      params={{ orgId, containerId, slug: c.slug }}
      search={{ tab: awaitsReview(c) ? "review" : "connection" }}
      className="flex w-full items-center gap-3 rounded-lg border bg-background p-4 text-left outline-none transition-[background-color,scale] hover:bg-muted/40 active:scale-[0.96] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
    >
      <ConnectorMonogram name={c.name} small />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold leading-tight">{c.name}</p>
        <p className="truncate text-xs text-muted-foreground">
          {c.account ?? "Not connected yet"} · <SourceLabel source={c.source} />
        </p>
      </div>
      <TileStatus c={c} />
    </Link>
  );
}

export function ConnectorGrid({ connectors, orgId, containerId }: { connectors: ConnectorSummary[]; orgId: string; containerId: string }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
      {connectors.map((c) => (
        <ConnectorTile key={c.slug} c={c} orgId={orgId} containerId={containerId} />
      ))}
    </div>
  );
}

// One line over the catalog while versions wait on a person.
export function ConnectorsWaitingNotice({ count, onOpen }: { count: number; onOpen: () => void }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-muted/30 px-4 py-3">
      <p className="inline-flex items-center gap-2 text-sm">
        <span className="size-1.5 rounded-full bg-amber-500" />
        {count === 1 ? "1 custom integration is waiting for you." : `${count} custom integrations are waiting for you.`}
      </p>
      <Button size="sm" variant="outline" onClick={onOpen}>
        Review
      </Button>
    </div>
  );
}
